import { buildCheckoutLines, createCartFingerprint, getEffectiveLegacyCartItems } from "./cartContext";

export const RECONCILE_CHECKOUT_CART = "RECONCILE_CHECKOUT_CART";
const number = value => Number.isFinite(Number(value)) ? Number(value) : 0;
const rounded = value => Number(value.toFixed(6));

// Validation is not just a view filter. Commit accepted quantities/removals to
// the persisted cart so every badge, inventory reservation and later edit agrees.
export const reconcileCart = (cart, validation, categories = []) => {
  if (!validation?.valid) return cart;
  const lines = buildCheckoutLines(cart);
  if (validation.cartFingerprint && validation.cartFingerprint !== createCartFingerprint(lines)) return cart;
  const regular = cart.cart || [];
  const promotional = cart.promo_items || [];
  const active = items => items.filter(item => number(item.quantity) > 0);
  const nextRegular = active(getEffectiveLegacyCartItems(regular, validation));
  const nextPromotional = active(getEffectiveLegacyCartItems(promotional, validation, regular.length));
  const changed = nextRegular.length !== regular.length || nextPromotional.length !== promotional.length ||
    nextRegular.some((item, i) => number(item.quantity) !== number(regular[i].quantity)) ||
    nextPromotional.some((item, i) => number(item.quantity) !== number(promotional[i].quantity));
  // Older clients could persist empty arrays with nonzero summary counters.
  // Repair that state too, without rewriting otherwise unchanged rich rows.
  const staleEmptyCart = !nextRegular.length && !nextPromotional.length && (
    ["price", "number_of_items", "charged_items", "delivered_items", "pickup_only_items",
      "delivery_only_items", "cash_only_items", "credit_only_items", "preordered_items",
      "preordering_max_leadtime"].some(key => number(cart[key]) !== 0) ||
    cart.smart_promos?.length || cart.promos || Object.keys(cart.categories_min_order || {}).length ||
    Object.values(cart.discounts || {}).some(value => Array.isArray(value) && value.length)
  );
  if (!changed && !staleEmptyCart) return cart;
  const all = [...nextRegular, ...nextPromotional];
  const count = flag => all.filter(item => item[flag]).length;
  const categoryTotals = {};
  const categoryIds = item => {
    const ids = new Set(item.category_id == null ? [] : [item.category_id]);
    for (const id of ids) categories.forEach(category => {
      if ((category.sub_categories || []).some(child => `${child}` === `${id}`)) ids.add(category.id);
    });
    return Array.from(ids);
  };
  const price = item => number(item.total_price ?? item.price) * number(item.quantity) + number(item.quantityExcludedOptionsTotal);
  nextRegular.forEach(item => {
    categoryIds(item).forEach(id => { categoryTotals[id] = rounded((categoryTotals[id] || 0) + price(item)); });
  });
  return {
    ...cart, cart: nextRegular, promo_items: nextPromotional,
    price: rounded(all.reduce((sum, item) => sum + price(item), 0)),
    number_of_items: rounded(all.reduce((sum, item) => sum + number(item.quantity), 0)),
    charged_items: count("charge_delivery"), delivered_items: count("is_delivered"),
    pickup_only_items: count("pickup_only"), delivery_only_items: count("delivery_only"),
    cash_only_items: count("cash_only"), credit_only_items: count("credit_only"),
    preordered_items: count("is_preordered"),
    preordering_max_leadtime: Math.max(0, ...all.map(item => number(item.preordering_leadtime))),
    categories_min_order: categoryTotals,
    mingled_item: all.some(item => item.no_mingling),
    non_mingled_category: nextRegular.reduce((selected, item) => selected ||
      (item.category_no_mingling ? item.category_id : null) ||
      categoryIds(item).find(id => categories.some(category => category.id === id && category.no_mingling)) || null, null),
    // Empty carts must not carry earned promotions or discount totals forward.
    ...(!all.length ? { smart_promos: [], promos: null,
      discounts: Object.keys(cart.discounts || {}).reduce((result, key) => ({ ...result, [key]: [] }), {}),
    } : {}),
  };
};
